import { c as _c } from "react/compiler-runtime";
import { Badge, Button, HStack, Icon, Separator, Text, VStack } from '@chakra-ui/react';
import { ClarityAccountSection } from '@src/components/sco/Advisors/clarityLink/ClarityAccountSection';
import { AdvisorProfileHubGate, AdvisorProfileLayout } from '@src/components/sco/Advisors/layout/AdvisorProfileLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SessionAvailabilitySection } from '@src/components/sco/Advisors/sections/SessionAvailabilitySection';
import { SessionSettingsSection } from '@src/components/sco/Advisors/sections/SessionSettingsSection';
import { SettingsFormSection } from '@src/components/sco/Advisors/shared/SettingsFormSection';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { LuMail } from 'react-icons/lu';
export const MatchEmailPrefsView = t0 => {
  const $ = _c(12);
  const {
    emailOn
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon boxSize={5} color="colorPalette.fg/55" flexShrink={0} marginTop={0.5}><LuMail /></Icon>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Text fontWeight="medium" textStyle="sm">Seat-match email</Text>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !emailOn ? " Match digests will not send while account email is off." : "";
  let t4;
  if ($[2] !== t3) {
    t4 = <VStack align="stretch" flex={1} gap={1} minW="14rem">{t2}<Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">Matched open-seat emails follow your account notifications. There is no separate advisor switch yet.{t3}</Text></VStack>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== emailOn) {
    t5 = emailOn ? <Badge colorPalette="teal" variant="subtle">Account email on</Badge> : <Badge colorPalette="red" variant="subtle">Account email off</Badge>;
    $[4] = emailOn;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Button asChild={true} size="sm" variant="outline"><NextLink href="/account/notifications">Account notifications</NextLink></Button>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <HStack flexShrink={0} gap={2}>{t5}{t6}</HStack>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <HStack align="flex-start" gap={4} wrap="wrap">{t1}{t4}{t7}</HStack>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};
const MatchEmailPrefs = () => {
  const $ = _c(2);
  const {
    currentUser
  } = useCurrentUser();
  const emailOn = currentUser?.emailNotificationSchedule !== "NEVER";
  let t0;
  if ($[0] !== emailOn) {
    t0 = <MatchEmailPrefsView emailOn={emailOn} />;
    $[0] = emailOn;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

/** `/advisors/profile/settings` — weekly hours, rates, match-email copy, Clarity.fm link. */
export const AdvisorProfileSettingsSection = () => {
  const $ = _c(10);
  const router = useRouter();
  const paths = useAdvisorsPaths();
  let t0;
  let t1;
  if ($[0] !== paths.ownProfile || $[1] !== paths.profilePayouts || $[2] !== router) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const id = window.location.hash.replace(/^#/, "");
      if (!id) {
        return;
      }
      if (id === "payouts") {
        router.replace(paths.profilePayouts);
        return;
      }
      if (id === "links") {
        router.replace(`${paths.ownProfile}#links`);
        return;
      }
      let tries = 0;
      let timer = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            block: "start"
          });
          return;
        }
        if (tries < 20) {
          tries = tries + 1;
          tries;
          timer = window.setTimeout(tick, 100);
        }
      };
      tick();
      return () => window.clearTimeout(timer);
    };
    t1 = [paths.ownProfile, paths.profilePayouts, router];
    $[0] = paths.ownProfile;
    $[1] = paths.profilePayouts;
    $[2] = router;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Separator />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SettingsFormSection description="When you prefer to take sessions, and whether founders can book you." id="availability" title="Booking availability"><SessionAvailabilitySection /></SettingsFormSection>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SettingsFormSection description="The listed hourly rate founders book against." id="rates" title="Session rates"><SessionSettingsSection /></SettingsFormSection>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SettingsFormSection description="Open-seat match mail uses your account email preference." id="notifications" title="Match emails"><MatchEmailPrefs /></SettingsFormSection>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AdvisorProfileHubGate><AdvisorProfileLayout compactHeader={true} activeKey="settings" description="When you can meet, rates, seat-match email, and your Clarity.fm account." title="Settings"><VStack align="stretch" gap={10} separator={t2}>{t3}{t4}{t5}<SettingsFormSection description="Link your Clarity.fm expert account to show your Clarity reviews." id="clarity" title="Clarity.fm account"><ClarityAccountSection /></SettingsFormSection></VStack></AdvisorProfileLayout></AdvisorProfileHubGate>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};